import { useCallback, useEffect, useRef, useState } from 'react';
import { MARK_HOVER_CLOSE_DELAY_MS, isMovingToHoverCard } from '../utils/mark-hover-utils';
import {
  mentionElementFromEvent,
  relatedEntityFromMentionElement,
  type RelatedEntity,
} from '../utils/entity-utils';

export type MentionHoverTarget = {
  entity: RelatedEntity;
  position: { top: number; left: number };
  mentionKey: string;
};

function mentionKeyFor(entity: RelatedEntity): string {
  return `${entity.type}:${entity.id}:${entity.documentId ?? ''}`;
}

function positionFromMention(mention: HTMLElement): { top: number; left: number } {
  const rect = mention.getBoundingClientRect();
  return {
    top: rect.bottom,
    left: rect.left + rect.width / 2,
  };
}

export function useMentionHover(): {
  hovered: MentionHoverTarget | null;
  hoverCardContentRef: React.RefObject<HTMLDivElement>;
  closeHover: () => void;
  onHoverCardMouseEnter: () => void;
  onHoverCardMouseLeave: () => void;
} {
  const [hovered, setHovered] = useState<MentionHoverTarget | null>(null);
  const hoverCardContentRef = useRef<HTMLDivElement | null>(null);
  const activeMentionRef = useRef<HTMLElement | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isOverMentionRef = useRef(false);
  const isOverCardRef = useRef(false);

  const cancelClose = useCallback((): void => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const closeHover = useCallback((): void => {
    cancelClose();
    activeMentionRef.current = null;
    isOverMentionRef.current = false;
    isOverCardRef.current = false;
    setHovered(null);
  }, [cancelClose]);

  const scheduleClose = useCallback((): void => {
    cancelClose();
    closeTimeoutRef.current = setTimeout(() => {
      if (!isOverMentionRef.current && !isOverCardRef.current) {
        closeHover();
      }
    }, MARK_HOVER_CLOSE_DELAY_MS);
  }, [cancelClose, closeHover]);

  const onHoverCardMouseEnter = useCallback((): void => {
    isOverCardRef.current = true;
    cancelClose();
  }, [cancelClose]);

  const onHoverCardMouseLeave = useCallback((): void => {
    isOverCardRef.current = false;
    scheduleClose();
  }, [scheduleClose]);

  useEffect(() => {
    const onPointerOver = (event: PointerEvent): void => {
      const mention = mentionElementFromEvent(event);
      if (!mention) {
        return;
      }
      const entity = relatedEntityFromMentionElement(mention);
      if (!entity) {
        if (activeMentionRef.current === mention) {
          closeHover();
        }
        return;
      }
      cancelClose();
      isOverMentionRef.current = true;
      if (activeMentionRef.current === mention) {
        return;
      }
      activeMentionRef.current = mention;
      setHovered({
        entity,
        position: positionFromMention(mention),
        mentionKey: mentionKeyFor(entity),
      });
    };

    const onPointerOut = (event: PointerEvent): void => {
      const mention = activeMentionRef.current;
      if (!mention) {
        return;
      }
      const target = event.target;
      if (!(target instanceof Node) || !mention.contains(target)) {
        return;
      }
      const related = event.relatedTarget;
      if (related instanceof Node && mention.contains(related)) {
        return;
      }
      const relatedElement = related instanceof HTMLElement ? related : null;
      isOverMentionRef.current = false;
      if (isMovingToHoverCard(relatedElement, hoverCardContentRef.current)) {
        return;
      }
      scheduleClose();
    };

    const onScrollOrHide = (): void => {
      closeHover();
    };

    document.addEventListener('pointerover', onPointerOver, true);
    document.addEventListener('pointerout', onPointerOut, true);
    window.addEventListener('scroll', onScrollOrHide, true);
    return () => {
      document.removeEventListener('pointerover', onPointerOver, true);
      document.removeEventListener('pointerout', onPointerOut, true);
      window.removeEventListener('scroll', onScrollOrHide, true);
      cancelClose();
      activeMentionRef.current = null;
    };
  }, [cancelClose, closeHover, scheduleClose]);

  return {
    hovered,
    hoverCardContentRef,
    closeHover,
    onHoverCardMouseEnter,
    onHoverCardMouseLeave,
  };
}
